import { useReducer } from "react";
import {
  jobPostingReducer,
  initialState,
  jobManagementReducer,
  jobManagementInitialState,
} from "./job.reducer";
import {
  changeApplicationStatus,
  closeJob,
  deleteJobs,
  getApplicationStatsByStatus,
  getJobApplicationDetails,
  getJobApplicationViewDetails,
  getJobsDetails,
  getJobsList,
  pauseJob,
  pendingApprovaliData,
  resumeJob,
  submitApprovalData,
  viewContactDetails,
} from "../api/job_api";
import {
  setLoading,
  setError,
  setJobs,
  setUsers,
  setShowModal,
  setApprovalFlows,
  hideApprovalPopup,
  setToast,
  setJOBToast,
  setApplicationTotal,
} from "./job.action";
import { useAuth } from "../../../../contexts/AuthContext";
import apiService from "../../../../services/api";
import { Job } from "../model/JobModel";
import { setOpenMenu, setPendingApprovals } from "./pendingAction.action";
import { getApprovals } from "../../CompanySettings/JobApproval/api/ApprovalFlow.api";

export const useJobsListViewModel = () => {
  const [state, dispatch] = useReducer(jobPostingReducer, initialState);
  const { user: currentUser } = useAuth();
  const showToastMessage = (
    message: string,
    type: "success" | "error" | "warning" | "info",
  ) => dispatch(setJOBToast({ isVisible: true, message, type }));
  const fetchJobs = async (params: any = {}) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));
      const resp = await getJobsList(params);
      dispatch(setJobs(resp.data.jobs));
      dispatch({
        type: "SET_JOB_SUMMARY",
        payload: {
          total: resp.data.total,
          active_jobs: resp.data.active_jobs,
          total_applications: resp.data.total_applications,
          has_more: resp.data.has_more,
        },
      });
    } catch (err) {
      console.log(err);
      dispatch(setError("Failed to fetch jobs"));
    } finally {
      dispatch(setLoading(false));
    }
  };

  const loadUsers = async () => {
    try {
      if (currentUser?.company_id) {
        const data = await apiService.getCompanyUsers(currentUser.company_id, {
          status_filter: "all",
        });
        dispatch(setUsers(data));
        console.log("data pass", data);
      }
    } catch (error) {
      console.log(error);
    }
  };
  const deleteJobsData = async () => {
    const id = state.deletingFlowId;
    if (!id) return;
    try {
      dispatch(setLoading(true));
      const resp = await deleteJobs(id);
      console.log(resp);
      dispatch(
        setJOBToast({
          isVisible: true,
          message: "Job deleted successfully",
          type: "success",
        }),
      );
      //showToastMessage("Job deleted successfully", "success");
      dispatch({ type: "CLOSE_DELETE_DIALOG" });
      await fetchJobs(getCurrentJobParams());
    } catch (error) {
      console.log(error);

      showToastMessage(
        "Cannot delete job in UNDER_REVIEW status. Only DRAFT jobs can be deleted.",
        "error",
      );
    } finally {
      dispatch(setLoading(false));
    }
  };
  const fetchJobDetails = async (id: string) => {
    try {
      dispatch(setLoading(true));
      dispatch(setError(null));

      const resp = await getJobsDetails(id);

      dispatch({
        type: "SET_JOB_DETAILS",
        payload: resp.data,
      });
    } catch (error) {
      console.error(error);
      dispatch(setError("Failed to fetch job details"));
    } finally {
      dispatch(setLoading(false));
    }
  };
  const fetchApplicationStatsByStatus = async (jobId: string) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });

      const resp = await getApplicationStatsByStatus(jobId);

      dispatch({
        type: "SET_APPLICATION_STATS",
        payload: resp.data,
      });
    } catch (error) {
      console.error(error);
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };
  const fetchJobApplications = async (params: any = {}) => {
    try {
      dispatch({ type: "SET_LOADING", payload: true });
      dispatch({ type: "SET_APPLICATION_QUERY", payload: params });

      const resp = await getJobApplicationDetails(params);

      dispatch({
        type: "SET_JOB_APPLICATIONS",
        payload: resp?.data?.applications || [],
      });
      dispatch(setApplicationTotal(resp?.data?.total || 0));
    } catch (error) {
      console.error(error);
    } finally {
      dispatch({ type: "SET_LOADING", payload: false });
    }
  };
  const handleViewApplication = async (
    applicationId: string,
    candidateId?: string,
  ) => {
    dispatch({ type: "OPEN_APPLICATION_VIEW", payload: true });

    dispatch({ type: "SET_VIEW_LOADING", payload: true });
    dispatch({ type: "SET_VIEW_APPLICATIONS", payload: null });

    try {
      if (candidateId) {
        try {
          await apiService.trackCandidateProfileView(candidateId);
        } catch (trackingError) {
          console.warn("Profile view tracking failed:", trackingError);
        }
      }

      const resp = await getJobApplicationViewDetails(applicationId);
      // 3. Data or null → both handled
      dispatch({
        type: "SET_VIEW_APPLICATIONS",
        payload: resp?.data ?? null,
      });
    } catch (error) {
      dispatch({
        type: "SET_VIEW_APPLICATIONS",
        payload: null,
      });
    } finally {
      dispatch({ type: "SET_VIEW_LOADING", payload: false });
    }
  };
  const handleViewContact = async (
    candidateId: string,
    applicationId: string,
  ) => {
    try {
      dispatch({ type: "SET_CANDIDATE_VIEW_LOADING", payload: true });
      if (!applicationId) {
        console.error("Application ID missing");
        return;
      }
      const payload = {
        access_type: "applicant",
        application_id: applicationId,
      };
      console.log("candidateId", payload);
      await viewContactDetails(candidateId, payload);
      const resp = await getJobApplicationViewDetails(applicationId);
      // if (state.applicationQuery) {
      //   await fetchJobApplications(state.applicationQuery);
      // }
      dispatch({
        type: "SET_VIEW_APPLICATIONS",
        payload: resp?.data ?? null,
      });
    } catch (error) {
      console.error("View contact failed", error);
    } finally {
      dispatch({ type: "SET_CANDIDATE_VIEW_LOADING", payload: false });
    }
  };
  const handleChangeApplicationStatus = async (
    applicationId: string,
    jobId: string,
    status: string,
  ) => {
    try {
      dispatch({ type: "SET_STATUS_UPDATE_LOADING", payload: true });
      dispatch(setOpenMenu(null));
      const payload = {
        status: status,
        recruiter_notes: "",
        feedback: "",
        rejection_reason: "",
      };
      const resp = await changeApplicationStatus(applicationId, payload);
      showToastMessage("Application status updated successfully", "success");
      dispatch({ type: "OPEN_APPLICATION_VIEW", payload: false });
      await fetchJobApplications(state.applicationQuery || {
        jobId: jobId,
        sort_by: state.applicationsort_by,
        include_profile: state.applicationInclude_profile,
        limit: state.applicationLimit,
        offset: (state.applicationCurrentPage - 1) * state.applicationLimit,
      });
      //await fetchJobApplications(jobId);
    } catch (error: any) {
      const apiMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Something went wrong";

      showToastMessage(apiMessage, "error");

      dispatch({ type: "OPEN_APPLICATION_VIEW", payload: false });
    } finally {
      dispatch({ type: "SET_STATUS_UPDATE_LOADING", payload: false });
    }
  };
  const getCurrentJobParams = () => ({
    limit: state.limit,
    offset: (state.currentPage - 1) * state.limit,
    status: state.statusFilter !== "all" ? state.statusFilter : undefined,
    search: state.search || undefined,
    title: state.title || undefined,
    location: state.location || undefined,
    department: state.department || undefined,
    hiring_manager_id: state.hiringManagerId || undefined,
    recruiter_id: state.recruiterId || undefined,
  });

  const fetchPausedJob = async (jobId: string) => {
    try {
      await pauseJob(jobId);
      showToastMessage("Job paused successfully", "success");
      await fetchJobs(getCurrentJobParams());
    } catch (error) {
      console.error(error);
    }
  };
  const fetchResumeJob = async (jobId: string) => {
    try {
      await resumeJob(jobId);
      showToastMessage("Job resumed successfully", "success");
      await fetchJobs(getCurrentJobParams());
    } catch (error) {
      console.error(error);
    }
  };
  const fetchCloseJob = async (jobId: string) => {
    try {
      await closeJob(jobId);
      showToastMessage("Job closed successfully", "success");
      await fetchJobs(getCurrentJobParams());
    } catch (error) {
      console.error(error);
    }
  };
  return {
    state,
    dispatch,
    fetchJobs,
    loadUsers,
    deleteJobsData,
    fetchJobDetails,
    fetchJobApplications,
    fetchApplicationStatsByStatus,
    handleViewApplication,
    handleViewContact,
    handleChangeApplicationStatus,
    fetchPausedJob,
    fetchResumeJob,
    fetchCloseJob,
  };
};

export const useJobCreateViewModel = () => {
  const [state, dispatch] = useReducer(
    jobManagementReducer,
    jobManagementInitialState,
  );
  const showToastMessage = (
    message: string,
    type: "success" | "error" | "warning" | "info",
  ) => dispatch(setToast({ isVisible: true, message, type }));

  const fetchApprovals = async (params: any = {}) => {
    try {
      // dispatch(setLoading(true));
      // dispatch(setError(null));
      const resp = await getApprovals();
      dispatch(setApprovalFlows(resp.data));
      console.log(resp);
      // dispatch(setTotal(resp.data.total));
    } catch (err) {
      console.log(err);
      //dispatch(setError("Failed to fetch Pools"));
    } finally {
      //dispatch(setLoading(false));
    }
  };
  const fetchSubmittedApprovals = async () => {
    if (!state.submittingJobId) return;

    try {
      const hasFlows = state.approvalFlows.length > 0;
      const resolvedFlowId =
        state.selectedApprovalFlowId ||
        (hasFlows
          ? String(
              (state.approvalFlows.find((f) => f.is_default) ??
                state.approvalFlows[0]).id,
            )
          : null);

      const payload = resolvedFlowId ? { approval_flow_id: resolvedFlowId } : {};
      await submitApprovalData(state.submittingJobId, payload);
      dispatch({ type: "HIDE_APPROVAL_POPUP" });

      dispatch(
        setToast({
          isVisible: true,
          message: "Approval flow assigned successfully",
          type: "success",
        }),
      );
    } catch (error) {
      console.error(error);
      dispatch(
        setToast({
          isVisible: true,
          message: "Failed to assign approval flow",
          type: "error",
        }),
      );
    } finally {
      //  dispatch({ type: "SET_LOADING", payload: false });
    }
  };

  return {
    state,
    dispatch,
    fetchApprovals,
    fetchSubmittedApprovals,
  };
};
